// Product section: variant changes and the media gallery.
// <product-page>: when a variant option changes, it finds the variant, updates the address and every variant id
// field, fires "product:variant", and re-renders the product blocks that show variant data ([data-product-part]).
// <product-gallery>: the slider (arrows, dots, counter, progress bar, thumbnails) and "go to the variant's image".
(() => {
  if (customElements.get('product-page')) return;

  class ProductPage extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent); } catch (error) { this.variants = []; }
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.onOptionChange();
      });
      // Quantity selectors in product blocks: - and + buttons around a number field.
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        const input = step?.closest('[data-qty]')?.querySelector('input');
        // Recommendation cards (Upsells block) handle their own quantity (assets/upsells.js).
        if (!input || step.closest('[data-card]')) return;
        input.value = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      // Quantity breaks tier chosen (assets/quantity-breaks.js): prices follow it; also once at load.
      this.addEventListener('quantity-breaks:change', () => this.showOffer());
      this.addEventListener('subscription:change', () => this.showOffer());
      requestAnimationFrame(() => this.showOffer());
      this.watchApps();
      if (window.Shopify?.designMode && document.body.dataset.editorWarnings !== 'off') this.editorWarnings();
    }

    // Theme editor only (shoppers never see it): setup problems of offer blocks (snippets/offer-warning.liquid: a lower
    // price without a discount code, a gift without a product, not added or not free). Each block with a problem gets a
    // red outline (no layout change), and the notes are listed in one red box at the top of its Quantity breaks /
    // subscription box (or of the block itself), each named after the item.
    editorWarnings() {
      this.querySelectorAll('.editor-warning--offer').forEach((box) => box.remove());
      const lists = new Map();
      this.querySelectorAll('[data-editor-warning]').forEach((element) => {
        element.style.outline = '2px solid rgb(200 16 46)';
        element.style.outlineOffset = '2px';
        const owner = element.closest('quantity-breaks, subscription-options') || element;
        const name = (element.querySelector('[data-qb-tier]')?.dataset.title || element.querySelector('[class*="title"]')?.textContent || '').trim().replace(/\s+/g, ' ');
        if (!lists.has(owner)) lists.set(owner, []);
        lists.get(owner).push(`${name ? `${name}: ` : ''}${element.dataset.editorWarning}`);
      });
      lists.forEach((notes, owner) => {
        const grid = owner.querySelector('.qb__grid');
        const box = Object.assign(document.createElement('div'), { className: `editor-warning editor-warning--offer${grid ? ' qb__cell qb__cell--full' : ''}` });
        box.style.cssText = 'order: -1; grid-column: 1 / -1; margin: 0 0 10px;';
        const inner = document.createElement('div');
        inner.style.cssText = 'display: grid; gap: 6px; padding: 8px 10px; border-radius: 6px; background: rgb(200 16 46); color: rgb(255 255 255); font-size: 12px; font-weight: 400; line-height: 1.35; text-align: left; text-transform: none; letter-spacing: 0;';
        [...notes, 'Outlined in red on the page. Only you see these notes, shoppers do not.'].forEach((text) => inner.append(Object.assign(document.createElement('p'), { textContent: text, style: 'margin: 0' })));
        box.append(inner);
        (grid || owner).prepend(box);
      });
    }

    disconnectedCallback() {
      this.appListeners?.abort();
      this.buttonObserver?.disconnect();
    }

    // ---------- Bundle and quantity apps (Amose, Kaching...) ----------
    // - Amose sends the bundle total with "amose:price-update" (its "Sync bundle price with theme" option): the price
    //   block, the price in the buy button and the sticky bar show it (this.appOffer, read by this.offer).
    // - Apps that write into the price elements themselves (Kaching: data-kaching-price...) keep what they wrote: a
    //   variant change draws the new variant price here first, then the apps react; the section re-render keeps them.
    // - Apps that put the button text back with textContent after adding (Amose) flatten the label, the price and the
    //   animation layers into plain text: the button gets its last full markup back.
    watchApps() {
      if (this.dataset.clientParts === 'true') return; // Featured product: apps work with the product page's own product
      this.appListeners = new AbortController();
      document.addEventListener('amose:price-update', (event) => {
        const { price, comparePrice } = event.detail || {};
        if (!(Number(price) > 0)) return;
        this.appOffer = { price: Number(price), compare: Number(comparePrice) || 0 };
        this.showOffer();
      }, { signal: this.appListeners.signal });
      const snapshots = new WeakMap();
      const keep = (button) => {
        if (button.querySelector('.button__label')) snapshots.set(button, { nodes: [...button.childNodes].map((node) => node.cloneNode(true)), text: button.textContent.trim() });
      };
      this.buttonObserver = new MutationObserver((records) => {
        new Set(records.map((record) => (record.target.nodeType === 1 ? record.target : record.target.parentElement)?.closest('[data-add-button]'))).forEach((button) => {
          if (!button) return;
          const saved = snapshots.get(button);
          // Flattened back to its own text (not a loading text the app shows meanwhile): rebuild the markup.
          if (!button.querySelector('.button__label') && saved && button.textContent.trim() === saved.text) button.replaceChildren(...saved.nodes.map((node) => node.cloneNode(true)));
          else keep(button);
        });
      });
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        keep(button);
        this.buttonObserver.observe(button, { childList: true, characterData: true, subtree: true });
      });
    }

    get selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        const input = group.querySelector('[data-option-input]:checked') || group.querySelector('select[data-option-input]');
        if (input) values[position] = input.value;
      });
      return values;
    }

    onOptionChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((value, i) => options[i] === undefined || options[i] === value)) || null;
      this.variant = variant;
      this.updateAvailability(options);
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const input = group.querySelector('[data-option-input]:checked') || group.querySelector('select[data-option-input]');
        const value = group.querySelector('[data-option-value]');
        if (value && input) value.textContent = input.value;
      });
      if (!variant) {
        this.dispatch(null);
        this.setButtons(false, true);
        return;
      }
      // The new price shows at once, and before the forms and the address change: bundle apps react to those and
      // write their own price after this one (see watchApps). An app price belongs to the previous variant.
      this.appOffer = null;
      this.showPrice(variant.price, variant.compare_at_price, variant.available);
      this.showOffer();
      this.buyForms.forEach((form) => form.querySelectorAll('input[name="id"], select[name="id"]').forEach((field) => { field.value = variant.id; field.dispatchEvent(new Event('change', { bubbles: true })); }));
      if (this.closest('.shopify-section') && !window.Shopify?.designMode && this.dataset.clientParts !== 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      this.dispatch(variant);
      this.setButtons(variant.available, false);
      if (this.dataset.clientParts === 'true') this.updateParts(variant);
      else this.refresh(variant.id);
    }

    // Featured product (any page): Shopify only renders a chosen variant on product pages, so the price, the price in
    // the buy button, the SKU and the inventory status follow the variant here in the browser.
    updateParts(variant) {
      this.showPrice(variant.price, variant.compare_at_price, variant.available);
      // SKU block: the variant's SKU, else the block's empty text; nothing at all hides the block's line.
      this.querySelectorAll('.sku__value').forEach((sku) => {
        const value = variant.sku || sku.dataset.empty || '';
        sku.textContent = value;
        sku.closest('.sku').hidden = !value;
      });
      // Inventory status block: state and text per variant are worked out in Liquid (data-variants).
      this.querySelectorAll('.inventory[data-variants]').forEach((box) => {
        let entry;
        try { entry = JSON.parse(box.dataset.variants)[variant.id]; } catch (error) { return; }
        if (!entry) return;
        const [state, text] = entry;
        box.querySelector('[data-inventory-text]').textContent = text;
        if (box.dataset.state === state) return;
        box.dataset.state = state;
        const icon = box.querySelector('.inventory__icon');
        const source = box.querySelector('template[data-inventory-icons]')?.content.querySelector(`[data-state="${state}"]`);
        if (!icon || !source) return;
        icon.replaceChildren(...source.cloneNode(true).childNodes);
        this.showGoogleIcons(icon);
      });
      // Pickup availability block: every variant's content waits in a template; none = the block hides.
      this.querySelectorAll('.pickup[data-pickup-variants]').forEach((box) => {
        const source = box.querySelector(`template[data-pickup-variant="${variant.id}"]`);
        const current = box.querySelector('[data-pickup-current]');
        const frame = box.closest('.block-frame');
        if (!current || !frame) return;
        current.replaceChildren(...(source ? source.content.cloneNode(true).childNodes : []));
        frame.hidden = !current.querySelector('.pickup__inner');
        this.showGoogleIcons(current);
      });
      this.showOffer();
    }

    // Google icons put in by script: shown once the icon font has turned the name into a glyph (as assets/icons.js).
    async showGoogleIcons(root) {
      const icons = [...root.querySelectorAll('.icon--google:not([data-ready])')];
      if (!icons.length) return;
      // assets/icons.js loads the icon font with the names on the page (these come from a template, so they are included)
      if (window.themeIcons) await window.themeIcons.load(root);
      // the font may not be registered right after the stylesheet switches on: retry (max 5 s)
      for (let i = 0; i < 50; i++) {
        const faces = await document.fonts.load('24px "Material Symbols Rounded"').catch(() => []);
        if (faces.length) break;
        await new Promise((done) => setTimeout(done, 100));
      }
      icons.forEach((icon) => {
        if (!icon.isConnected) return;
        const glyph = icon.querySelector('.icon__glyph');
        if (glyph && glyph.scrollWidth > glyph.clientWidth * 1.2) icon.remove();
        else icon.setAttribute('data-ready', '');
      });
    }

    // The price block and the price in the buy button show what is being bought (this.offer), like the sticky bar.
    // Without Quantity breaks or a subscription block the Liquid prices stay as they are.
    showOffer() {
      if (!this.appOffer && !this.querySelector('quantity-breaks, subscription-options')) return;
      const offer = this.offer;
      if (offer) this.showPrice(offer.price, offer.compare, offer.available);
      // The sticky bar follows an app's price too (assets/sticky-cart.js).
      if (this.appOffer) this.dispatchEvent(new CustomEvent('product:offer', { bubbles: true, detail: offer }));
    }

    // What the shopper is buying now and its price: the selected Quantity breaks tier (total, with the subscription
    // price while subscribing); without one, a Subscription plans pack (its total) or the Subscribe & save price per
    // item while subscribing; otherwise the variant. Read by the price block, the buy button and the sticky bar.
    get offer() {
      const variant = this.currentVariant;
      const available = variant ? variant.available : true;
      if (this.appOffer) return { ...this.appOffer, available };
      const tier = this.querySelector('quantity-breaks')?.offer;
      // The tier plus its ticked upsells (one-time items, so never at the subscription price).
      if (tier && tier.total > 0) return { price: (tier.current || tier.total) + (tier.extra || 0), compare: tier.compare + (tier.extraCompare || 0), available };
      if (!variant) return null;
      const regular = Math.max(variant.compare_at_price || 0, variant.price);
      const box = this.querySelector('subscription-options[data-subscribed="true"]');
      if (box?.dataset.mode === 'plans') {
        const chosen = box.querySelector('[data-sub-choice]:checked');
        const cents = Number(chosen?.dataset.cents) || 0;
        if (cents > 0) return { price: cents, compare: regular * (Number(chosen.dataset.quantity) || 1), available };
      }
      const price = this.subscriptionPrice;
      if (price > 0) return { price, compare: regular, available };
      return { price: variant.price, compare: variant.compare_at_price || 0, available };
    }

    // Add to cart forms of this product (buy buttons, installments). Not the cart drawer, the sticky bar, or a Button
    // block that adds another product (its form carries data-product-id).
    get buyForms() {
      return [...this.querySelectorAll('form[action*="/cart/add"]')].filter((form) => !form.closest('cart-drawer, sticky-cart')
        && (!form.dataset.productId || form.dataset.productId === this.dataset.productId));
    }

    // The form that adds to the cart: the buy buttons, else the first form of this product.
    get buyForm() {
      const forms = this.buyForms;
      return forms.find((form) => form.querySelector('[data-add-button]')) || forms[0] || null;
    }

    // The variant in the buy form (the one the page shows).
    get currentVariant() {
      const id = Number(this.buyForm?.querySelector('[name="id"]')?.value);
      return this.variants?.find((v) => v.id === id) || this.variant || null;
    }

    // Price per item of the chosen Subscribe & save frequency, 0 while the one-time purchase is chosen.
    get subscriptionPrice() {
      const box = this.querySelector('subscription-options[data-mode="box"]');
      if (!box || box.dataset.subscribed !== 'true') return 0;
      const source = box.querySelector('select[data-sub-frequency]')?.selectedOptions[0] || box.querySelector('input[data-sub-frequency]:checked');
      return Number(source?.dataset.cents) || 0;
    }

    showPrice(price, comparePrice, available) {
      const money = (cents, el) => window.theme.formatMoney(cents, el.dataset.moneyFormat);
      const onSale = comparePrice > price;
      const variant = { price, compare_at_price: comparePrice, available };
      const hooks = this.dataset.clientParts !== 'true'; // app hooks only on the product page's own product
      this.querySelectorAll('.product-price[data-money-format]').forEach((block) => {
        const current = block.querySelector('.product-price__current');
        const row = block.querySelector('.product-price__row');
        if (!current || !row) return;
        current.textContent = money(variant.price, block);
        current.classList.toggle('product-price__current--sale', onSale);
        row.querySelector('.product-price__compare')?.remove();
        row.querySelector('.product-price__badge')?.remove();
        // The compare price stays in the row while hidden: bundle apps show their own there (snippets/block-product-price.liquid).
        const compare = document.createElement('s');
        compare.className = 'product-price__compare';
        if (hooks) compare.setAttribute('data-kaching-price-compare', '');
        compare.textContent = onSale ? money(variant.compare_at_price, block) : '';
        if (!onSale) compare.style.display = 'none';
        current.after(compare);
        if (!onSale) return;
        const kind = block.dataset.badge;
        if (kind === 'none') return;
        const badge = document.createElement('span');
        badge.className = 'product-price__badge';
        if (hooks && kind === 'percent') badge.setAttribute('data-kaching-price-badge', '');
        const saved = variant.compare_at_price - variant.price;
        badge.textContent = kind === 'percent' ? `${Math.round((saved * 100) / variant.compare_at_price)}% ${block.dataset.badgeLabel}`
          : kind === 'custom' ? block.dataset.badgeText : `${money(saved, block).replace(/[.,]00(?=\D*$)/, '')} ${block.dataset.badgeLabel}`;
        compare.after(badge);
      });
      this.querySelectorAll('.buy-buttons__price[data-money-format]').forEach((price) => {
        price.replaceChildren();
        if (!variant.available) return;
        const separator = document.createElement('span');
        separator.className = 'buy-buttons__separator';
        separator.textContent = price.dataset.separator;
        const amount = document.createElement('span');
        amount.textContent = money(variant.price, price);
        price.append(separator, amount);
        if (price.dataset.showCompare === 'true') {
          // Kept while hidden, with a price in it: bundle apps (Kaching) find it and show the bundle's full price here.
          const compare = document.createElement('s');
          compare.className = 'buy-buttons__compare';
          compare.textContent = money(onSale ? variant.compare_at_price : variant.price, price);
          if (!onSale) compare.style.display = 'none';
          price.append(compare);
        }
      });
    }

    dispatch(variant) {
      this.dispatchEvent(new CustomEvent('product:variant', { bubbles: true, detail: { variant, sectionId: this.dataset.sectionId } }));
      if (variant?.featured_media) this.querySelector('product-gallery')?.goToMedia(variant.featured_media.id);
    }

    // Marks option values that have no available variant with the other current choices.
    updateAvailability(options) {
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        group.querySelectorAll('[data-option-input], option[data-option-value-item]').forEach((input) => {
          const test = [...options];
          test[position] = input.value;
          const match = this.variants.find((v) => v.options.every((value, i) => test[i] === undefined || test[i] === value));
          const state = !match ? 'missing' : match.available ? 'available' : 'sold-out';
          (input.closest('[data-option-item]') || input).dataset.state = state;
          // Dropdown values: " — Unavailable" suffix, and Hide drops them from the list (buttons/swatches: CSS).
          if (input.tagName !== 'OPTION') return;
          const label = input.parentElement.dataset.unavailableLabel;
          input.textContent = state === 'available' || !label ? input.value : `${input.value} — ${label}`;
          const hide = group.dataset.unavailable === 'hide' && state !== 'available' && !input.selected;
          input.hidden = hide;
          input.disabled = hide;
        });
      });
    }

    setButtons(available, missing) {
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-add-label]');
        if (!label) return;
        label.textContent = missing ? button.dataset.unavailableText : available ? button.dataset.addText : button.dataset.soldOutText;
      });
    }

    async refresh(variantId) {
      this.request?.abort();
      this.request = new AbortController();
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const html = await fetch(url, { signal: this.request.signal }).then((response) => response.text());
        const next = new DOMParser().parseFromString(html, 'text/html');
        next.querySelectorAll('[data-product-part]').forEach((fresh) => {
          const current = this.querySelector(`[data-product-part="${fresh.dataset.productPart}"]`);
          if (!current || current.contains(document.activeElement)) return;
          // The price and the price in the buy button were drawn for this variant already (onOptionChange), and a
          // bundle app may have written its price there since: they stay. The rest of the block (unit price,
          // installments...) is the fresh copy.
          if (current.matches('.buy-buttons__price')) return;
          const row = current.querySelector('.product-price__row');
          if (row) fresh.querySelector('.product-price__row')?.replaceWith(row);
          current.replaceWith(fresh);
        });
        this.showOffer();
        if (window.Shopify?.designMode && document.body.dataset.editorWarnings !== 'off') this.editorWarnings();
        document.dispatchEvent(new CustomEvent('product:rendered', { detail: { sectionId: this.dataset.sectionId } }));
      } catch (error) {
        /* aborted or offline: the previous content stays */
      }
    }
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.track.children];
      this.style.setProperty('--gallery-total', this.slides.length);
      this.addEventListener('click', (event) => {
        const go = event.target.closest('[data-gallery-go]');
        const step = event.target.closest('[data-gallery-step]');
        if (go) this.goTo(Number(go.dataset.galleryGo));
        if (step) this.goTo(this.index + Number(step.dataset.galleryStep));
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.enableDrag();
      if (this.dataset.zoom === 'true') this.enableZoom();
      const current = this.slides.findIndex((slide) => slide.hasAttribute('data-current'));
      this.index = 0;
      if (current > 0) this.goTo(current, false);
      this.sync();
      // Arrows on the sides centre on the media itself, also when the controls row sits below it (--_media-h).
      const viewport = this.querySelector('.gallery__viewport');
      if (viewport) {
        const measure = () => this.style.setProperty('--_media-h', `${viewport.offsetHeight}px`);
        measure();
        this.mediaObserver = new ResizeObserver(measure);
        this.mediaObserver.observe(viewport);
      }
    }

    disconnectedCallback() {
      this.mediaObserver?.disconnect();
    }

    // Mouse drag (touch scrolls natively): snap is paused while dragging, then the nearest slide in the drag direction is shown.
    enableDrag() {
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let startIndex = 0;
      const move = (event) => {
        moved = event.clientX - startX;
        if (Math.abs(moved) > 4) this.classList.add('is-dragging');
        this.track.scrollLeft = startLeft - moved;
      };
      const end = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        if (!this.classList.contains('is-dragging')) return;
        this.classList.remove('is-dragging');
        this.dragged = true;
        const width = this.slides[0]?.offsetWidth || 1;
        const step = Math.abs(moved) > width * 0.15 ? (moved < 0 ? 1 : -1) : 0;
        this.goTo(startIndex + step);
        setTimeout(() => { this.dragged = false; }, 0);
      };
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.sliding || event.target.closest('button, input, video, model-viewer, iframe')) return;
        event.preventDefault();
        startX = event.clientX;
        startLeft = this.track.scrollLeft;
        startIndex = this.index;
        this.gliding = null;
        moved = 0;
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', end);
      });
      this.track.addEventListener('click', (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    }

    // "Zoom images on click": the image full screen with arrows and the counter on it (template in blocks/_product-gallery.liquid,
    // moved to <body> so no section clips it). Images only; a mouse drag of the slider does not open it (the drag click is
    // stopped above). Esc / backdrop / close button close it; arrow keys and a swipe change the image; the gallery then shows
    // the last image viewed.
    enableZoom() {
      const template = this.querySelector('template[data-gallery-zoom]');
      if (!template) return;
      this.track.addEventListener('click', (event) => {
        const slide = event.target.closest('.gallery__slide[data-zoom-src]');
        if (!slide || event.target.closest('button, a, .gallery__boxes')) return;
        this.openZoom(slide);
      });
    }

    openZoom(slide) {
      if (!this.zoom) {
        this.zoom = this.querySelector('template[data-gallery-zoom]').content.firstElementChild.cloneNode(true);
        this.zoom.setAttribute('style', this.getAttribute('style') || ''); // the gallery's Custom slider-control values
        document.body.append(this.zoom);
        const step = (by) => this.showZoom(this.zoomIndex + by);
        this.zoom.addEventListener('click', (event) => {
          const button = event.target.closest('[data-zoom-step]');
          if (button) step(Number(button.dataset.zoomStep));
          else if (event.target.closest('[data-zoom-close]') || event.target === this.zoom) this.zoom.close();
        });
        this.zoom.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') step(-1);
          if (event.key === 'ArrowRight') step(1);
        });
        // drag (mouse) / swipe (touch) on the image: it follows the pointer, past 50px the next / previous image shows
        const media = this.zoom.querySelector('.gzoom__media');
        const image = this.zoom.querySelector('.gzoom__image');
        let startX = null;
        let dx = 0;
        media.addEventListener('pointerdown', (event) => {
          if (event.button !== 0 || this.zoomSlides.length < 2) return;
          startX = event.clientX;
          dx = 0;
          media.setPointerCapture(event.pointerId);
        });
        media.addEventListener('pointermove', (event) => {
          if (startX === null) return;
          dx = event.clientX - startX;
          if (Math.abs(dx) > 4) this.zoom.classList.add('is-dragging');
          image.style.transform = `translateX(${dx}px)`;
        });
        const release = () => {
          if (startX === null) return;
          startX = null;
          image.style.transform = '';
          this.zoom.classList.remove('is-dragging');
          if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
        };
        media.addEventListener('pointerup', release);
        media.addEventListener('pointercancel', release);
        this.zoom.addEventListener('close', () => {
          document.documentElement.classList.remove('gzoom-lock');
          const shown = this.zoomSlides[this.zoomIndex];
          if (shown) this.goTo(this.slides.indexOf(shown), false);
        });
      }
      this.zoomSlides = this.slides.filter((item) => item.dataset.zoomSrc);
      this.zoom.querySelector('[data-zoom-total]').textContent = this.zoomSlides.length;
      this.zoom.dataset.single = String(this.zoomSlides.length < 2);
      document.documentElement.classList.add('gzoom-lock');
      this.zoom.showModal();
      this.zoom.focus(); // no arrow looks pressed when it opens; arrow keys still work
      this.showZoom(this.zoomSlides.indexOf(slide));
    }

    showZoom(index) {
      const total = this.zoomSlides.length;
      this.zoomIndex = (index + total) % total; // runs round at both ends
      const slide = this.zoomSlides[this.zoomIndex];
      const image = this.zoom.querySelector('.gzoom__image');
      image.srcset = slide.dataset.zoomSrcset;
      image.src = slide.dataset.zoomSrc;
      image.alt = slide.dataset.zoomAlt || '';
      if (slide.dataset.zoomWidth) { image.width = Number(slide.dataset.zoomWidth); image.height = Number(slide.dataset.zoomHeight); }
      this.zoom.querySelector('[data-zoom-current]').textContent = this.zoomIndex + 1;
      [1, -1].forEach((by) => { const near = this.zoomSlides[(this.zoomIndex + by + total) % total]; if (near) { const pre = new Image(); pre.sizes = '(max-width: 749px) 92vw, 90vw'; pre.srcset = near.dataset.zoomSrcset; } });
    }

    // Left padding of the track (the mobile peek slider runs to the screen edge, see product.css).
    get pad() {
      return parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    }

    get sliding() {
      return this.track.scrollWidth > this.track.clientWidth + 1;
    }

    goTo(index, smooth = true) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[target];
      if (!slide) return;
      if (this.sliding) {
        const left = slide.offsetLeft - this.track.offsetLeft - this.pad;
        this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
        // While it glides there, scroll events must not pick the slides it passes (a quick second click would start
        // from the old slide again). Ends when the track gets there, or after a moment (e.g. the visitor took over).
        this.gliding = { left: Math.min(Math.max(left, 0), this.track.scrollWidth - this.track.clientWidth), until: performance.now() + 900 };
      } else slide.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'nearest' });
      this.setIndex(target);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    sync() {
      if (!this.sliding) return;
      if (this.gliding) {
        const wait = this.gliding.until - performance.now();
        if (Math.abs(this.track.scrollLeft - this.gliding.left) > 2 && wait > 0) {
          clearTimeout(this.glideTimer);
          this.glideTimer = setTimeout(() => this.sync(), wait);
          return;
        }
        this.gliding = null;
      }
      const left = this.track.scrollLeft + this.pad;
      let best = 0;
      this.slides.forEach((slide, i) => {
        if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.slides[best].offsetLeft - this.track.offsetLeft - left)) best = i;
      });
      this.setIndex(best);
    }

    setIndex(index) {
      // the thumbnail strip only glides when the slide changes (scroll frames call this over and over)
      const changed = index !== this.index;
      this.index = index;
      this.style.setProperty('--_index', index);
      const current = this.querySelector('[data-gallery-current]');
      if (current) current.textContent = index + 1;
      this.querySelectorAll('[data-gallery-go]').forEach((button) => button.setAttribute('aria-current', String(Number(button.dataset.galleryGo) === index)));
      const prev = this.querySelector('[data-gallery-step="-1"]');
      const next = this.querySelector('[data-gallery-step="1"]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.slides.length - 1;
      const thumb = this.querySelector(`.gallery__thumb[data-gallery-go="${index}"]`);
      const strip = this.querySelector('[data-gallery-thumbs]');
      if (changed && thumb && strip && strip.scrollWidth + strip.scrollHeight > strip.clientWidth + strip.clientHeight) {
        strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, top: thumb.offsetTop - strip.offsetTop - strip.clientHeight / 2 + thumb.clientHeight / 2, behavior: 'smooth' });
      }
    }
  }

  customElements.define('product-page', ProductPage);
  customElements.define('product-gallery', ProductGallery);
})();
